/**
 * 用户活动监测器
 * 用于检测用户是否处于活跃状态，防止空闲时间被计入阅读时长
 */
class ActivityMonitor {
  constructor(options = {}) {
    // 空闲超时时间（毫秒），默认10分钟
    this.idleTimeout = options.idleTimeout || 10 * 60 * 1000;
    // 当前是否活跃
    this.isActive = true;
    // 空闲计时器
    this.idleTimer = null;
    // 是否已初始化
    this.initialized = false;
    // iframe 检查定时器
    this.iframeCheckTimer = null;
    // 已绑定事件的 iframe 列表
    this.boundIframes = new Set();
    // 活动事件列表
    this.activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'click',
      'touchstart',
      'touchmove',
      'wheel'
    ];
    // 绑定的事件处理函数（用于移除）
    this.boundHandleActivity = this.handleActivity.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  /**
   * 初始化监测器
   */
  init() {
    if (this.initialized) return;
    
    // 监听主文档的用户活动事件
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.boundHandleActivity, { passive: true });
    });
    
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);
    
    // 监听窗口焦点变化
    window.addEventListener('blur', () => {
      this.isActive = false;
    });
    window.addEventListener('focus', () => {
      this.resetIdleTimer();
    });
    
    // 定期检查并绑定 iframe 事件（因为 iframe 可能动态加载）
    this.iframeCheckTimer = setInterval(() => {
      this.bindIframeEvents();
    }, 1000);
    
    // 立即执行一次
    this.bindIframeEvents();
    
    // 启动空闲计时器
    this.resetIdleTimer();
    this.initialized = true;
    
    console.log('[ActivityMonitor] 初始化完成，空闲超时时间:', this.idleTimeout / 1000, '秒');
  }

  /**
   * 绑定 iframe 内的事件监听
   */
  bindIframeEvents() {
    try {
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        // 跳过已绑定的 iframe
        if (this.boundIframes.has(iframe)) return;
        
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc && iframeDoc.body) {
            // 监听 iframe 内的活动事件
            this.activityEvents.forEach(event => {
              iframeDoc.addEventListener(event, this.boundHandleActivity, { passive: true });
            });
            this.boundIframes.add(iframe);
            console.log('[ActivityMonitor] 已绑定 iframe 事件, iframe:', iframe.src || iframe.id || 'unknown');
          }
        } catch (e) {
          // 跨域 iframe 无法访问，忽略
          console.log('[ActivityMonitor] 无法绑定 iframe (可能跨域):', e.message);
        }
      });
    } catch (e) {
      // 忽略错误
    }
  }

  /**
   * 处理用户活动
   */
  handleActivity() {
    if (!this.isActive) {
      console.log('[ActivityMonitor] 检测到用户活动，恢复计时');
    }
    this.resetIdleTimer();
  }

  /**
   * 处理页面可见性变化
   */
  handleVisibilityChange() {
    if (document.hidden) {
      this.isActive = false;
      console.log('[ActivityMonitor] 页面不可见，暂停计时');
    } else {
      this.resetIdleTimer();
      console.log('[ActivityMonitor] 页面可见，恢复计时');
    }
  }

  /**
   * 重置空闲计时器
   */
  resetIdleTimer() {
    this.isActive = true;
    
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    
    this.idleTimer = setTimeout(() => {
      this.isActive = false;
      console.log('[ActivityMonitor] 用户空闲，暂停计时');
    }, this.idleTimeout);
  }

  /**
   * 检查用户是否活跃
   * @returns {boolean}
   */
  checkIsActive() {
    return this.isActive && !document.hidden;
  }

  /**
   * 销毁监测器，移除所有事件监听
   */
  destroy() {
    if (!this.initialized) return;
    
    // 移除主文档的活动事件监听
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.boundHandleActivity);
    });
    
    // 移除可见性监听
    document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
    
    // 移除 iframe 内的事件监听
    this.boundIframes.forEach(iframe => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          this.activityEvents.forEach(event => {
            iframeDoc.removeEventListener(event, this.boundHandleActivity);
          });
        }
      } catch (e) {
        // 跨域 iframe 无法访问，忽略
      }
    });
    this.boundIframes.clear();
    
    // 清除 iframe 检查定时器
    if (this.iframeCheckTimer) {
      clearInterval(this.iframeCheckTimer);
      this.iframeCheckTimer = null;
    }
    
    // 清除空闲计时器
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    
    this.initialized = false;
    console.log('[ActivityMonitor] 已销毁');
  }
}

// 创建单例实例
let activityMonitorInstance = null;

/**
 * 获取 ActivityMonitor 单例
 * @param {Object} options - 配置选项
 * @returns {ActivityMonitor}
 */
export const getActivityMonitor = (options = {}) => {
  if (!activityMonitorInstance) {
    activityMonitorInstance = new ActivityMonitor(options);
  }
  return activityMonitorInstance;
};

/**
 * 销毁 ActivityMonitor 单例
 */
export const destroyActivityMonitor = () => {
  if (activityMonitorInstance) {
    activityMonitorInstance.destroy();
    activityMonitorInstance = null;
  }
};

export default ActivityMonitor;

